import { useTranslations } from '@vocab/react';
import { IconDelete, Box, ButtonIcon } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { type ReactNode } from 'react';

import { DeleteConfirmationDialog } from 'src/components/common/DeleteConfirmation';
import Loader from 'src/components/common/Loader';

import translations from '../../.vocab';
import type { ItemType } from '../Item';

import * as styles from './ItemContent.css';

interface Props {
  type: ItemType;
  canDelete: boolean;
  onClickDelete: () => void;
  loading: boolean;
  children: ReactNode;
  isConfirmingDelete: boolean;
  onDeleteConfirmed: () => void;
  onDeleteCancelled: () => void;
}

const ItemContent = ({
  children,
  type,
  canDelete,
  loading,
  isConfirmingDelete,
  onClickDelete,
  onDeleteConfirmed,
  onDeleteCancelled,
}: Props) => {
  const { t } = useTranslations(translations);

  if (loading) {
    return (
      <div data-testid="loader">
        <Loader />
      </div>
    );
  }

  if (isConfirmingDelete) {
    return (
      <DeleteConfirmationDialog
        onDelete={onDeleteConfirmed}
        onCancel={onDeleteCancelled}
        type={type}
      >
        {t('Are you sure you want to delete this....', {
          type,
        })}
      </DeleteConfirmationDialog>
    );
  }

  return (
    <Box
      aria-label="item-content"
      flexGrow={1}
      padding="none"
      paddingLeft="medium"
      display="flex"
    >
      <div className={styles.itemContent} data-testid={`${type}-content`}>
        <div>{children}</div>
      </div>

      {canDelete && (
        <ButtonIcon
          id="delete_note"
          label={
            type === 'attachment' ? t('Delete attachment') : t('Delete note')
          }
          icon={<IconDelete tone="neutral" />}
          data={{ testid: `delete-${type}` }}
          variant="transparent"
          size="small"
          onClick={onClickDelete}
        />
      )}
    </Box>
  );
};

export default observer(ItemContent);
